/**
 * PortalDomainSection (Story 15.5 — Custom Portal Domain, Agency)
 *
 * Self-contained Agency-only settings sub-panel for the white-label portal domain.
 * Manages its own data (react-query against /api/tenant/branding/portal-domain). The
 * agency enters a domain → we provision a Cloudflare for SaaS custom hostname → show
 * the single CNAME to publish; Cloudflare auto-issues the TLS cert once it resolves.
 * A status badge + "Check verification" button (and a poll cron) advance the state.
 *
 * Non-agency plans see an upgrade hint. SECURITY: the verified domain is only ever a
 * branding hint server-side — the portal token remains the sole authorization.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input, Label } from "@provenleap/ui";
import type { Plan } from "@provenleap/types";
import {
  fetchPortalDomain,
  updatePortalDomain,
  verifyPortalDomain,
  TenantBrandingConflictError,
  TenantBrandingValidationError,
  type PortalDomainProjection,
  type PortalDomainStatus,
} from "../../lib/tenantBrandingApi";

const QUERY_KEY = ["tenant", "portal-domain"] as const;

const STATUS_LABEL: Record<PortalDomainStatus, string> = {
  pending: "Pending DNS",
  verifying: "Provisioning SSL…",
  verified: "Live",
  failed: "Verification failed",
};

const STATUS_CLASS: Record<PortalDomainStatus, string> = {
  pending: "border-amber-300 text-amber-900 bg-amber-50",
  verifying: "border-amber-300 text-amber-900 bg-amber-50",
  verified: "border-emerald-300 text-emerald-900 bg-emerald-50",
  failed: "border-red-300 text-red-900 bg-red-50",
};

function StatusBadge({ status }: { status: PortalDomainStatus }) {
  return (
    <span
      data-testid="portal-domain-status"
      className={`inline-flex items-center rounded-full border-[0.5px] px-2.5 py-0.5 text-[11px] font-medium ${STATUS_CLASS[status]}`}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

export function PortalDomainSection({ plan }: { plan: Plan }) {
  // White-label is a paid feature (all tiers except the free "Solo" plan).
  if (plan === "free") {
    return (
      <div className="space-y-2">
        <Label htmlFor="portalDomain">Custom portal domain</Label>
        <Input id="portalDomain" disabled value="" placeholder="portal.youragency.com" />
        <p className="text-[12px] text-muted-foreground">
          Let clients access the portal on your own domain — available on every paid plan.{" "}
          <Link to="/settings/billing" className="underline underline-offset-2">
            Upgrade your plan
          </Link>
        </p>
      </div>
    );
  }
  return <PortalDomainForm />;
}

function PortalDomainForm() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchPortalDomain,
  });

  const [domain, setDomain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Seed the input ONCE; post-save re-sync happens in onSuccess (single field, so no
  // cross-field clobber risk — but we keep the discipline consistent with the email
  // sender section). Verify writes no input field.
  const seededRef = useRef(false);
  useEffect(() => {
    if (data && !seededRef.current) {
      setDomain(data.customDomain ?? "");
      seededRef.current = true;
    }
  }, [data]);

  function onSettled(next: PortalDomainProjection) {
    queryClient.setQueryData(QUERY_KEY, next);
  }

  function handleError(err: unknown) {
    if (err instanceof TenantBrandingValidationError) {
      setError(err.fields[0]?.message ?? err.message);
    } else if (err instanceof TenantBrandingConflictError) {
      // Covers both stale-update and domain-taken (409). Surface the server detail.
      setError(err.message);
      seededRef.current = false;
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    } else {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  const saveMutation = useMutation({
    mutationFn: (customDomain: string | null) =>
      updatePortalDomain({ customDomain, updatedAt: data!.updatedAt }),
    onSuccess: (next) => {
      setError(null);
      setNotice(next.customDomain ? "Saved." : "Custom domain removed.");
      setDomain(next.customDomain ?? "");
      onSettled(next);
    },
    onError: (err) => {
      setNotice(null);
      handleError(err);
    },
  });

  const verifyMutation = useMutation({
    mutationFn: verifyPortalDomain,
    onSuccess: (next) => {
      setError(null);
      setNotice(
        next.customDomainStatus === "verified"
          ? "Domain is live."
          : "Still provisioning — DNS + SSL can take a little while after you add the CNAME.",
      );
      onSettled(next);
    },
    onError: (err) => {
      setNotice(null);
      handleError(err);
    },
  });

  if (isLoading) {
    return <p className="text-[12px] text-muted-foreground">Loading domain settings…</p>;
  }
  if (isError || !data) {
    return (
      <p className="text-[12px] text-red-900">
        Could not load portal domain settings. Please refresh.
      </p>
    );
  }

  const status = data.customDomainStatus;
  const hasDomain = Boolean(data.customDomain);
  const busy = saveMutation.isPending || verifyMutation.isPending;
  const trimmed = domain.trim();
  const dirty = trimmed !== (data.customDomain ?? "");

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="portalDomain">Custom portal domain</Label>
          {hasDomain && status ? <StatusBadge status={status} /> : null}
        </div>
        <div className="flex items-center gap-2">
          <Input
            id="portalDomain"
            value={domain}
            placeholder="portal.youragency.com"
            spellCheck={false}
            onChange={(e) => setDomain(e.target.value)}
          />
          <Button
            type="button"
            disabled={busy || !dirty}
            onClick={() => saveMutation.mutate(trimmed.length > 0 ? trimmed : null)}
          >
            {saveMutation.isPending ? "Saving…" : hasDomain && !trimmed ? "Remove" : "Save"}
          </Button>
        </div>
        <p className="text-[12px] text-muted-foreground">
          Clients will reach the portal at your own domain once it's verified. Until
          then, links keep working on the default ProvenLeap URL.
        </p>
      </div>

      {hasDomain && data.dnsRecords.length > 0 && status !== "verified" ? (
        <div className="space-y-2 rounded-2xl border-[0.5px] border-[var(--hairline)] p-3">
          <p className="text-[12px] font-medium">
            Add this CNAME record at your DNS provider — we'll provision the SSL
            certificate automatically:
          </p>
          {data.dnsRecords.map((rec) => (
            <div
              key={rec.name}
              data-testid="portal-dns-record"
              className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px] font-mono break-all"
            >
              <span className="text-muted-foreground">Type</span>
              <span>{rec.type}</span>
              <span className="text-muted-foreground">Name</span>
              <span>{rec.name}</span>
              <span className="text-muted-foreground">Value</span>
              <span>{rec.value}</span>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => verifyMutation.mutate()}
          >
            {verifyMutation.isPending ? "Checking…" : "Check verification"}
          </Button>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="text-[12px] text-red-900">
          {error}
        </p>
      ) : null}
      {notice && !error ? (
        <p role="status" className="text-[12px] text-emerald-900">
          {notice}
        </p>
      ) : null}
    </div>
  );
}
